import type { TranslationKey } from "../_generated/translation-keys";
import type { ICustomFieldsModel } from "./customFields.model";

export type CustomFieldValueType = "string" | "number" | "boolean" | "date" | "dateTime";

export interface CustomFieldTranslatedText {
  translationKey: TranslationKey;
}

// Some organizations' forms show a hardcoded German text instead of a translation.
export interface CustomFieldFixedText {
  text: string;
}

export type CustomFieldText = CustomFieldTranslatedText | CustomFieldFixedText;

export interface CustomFieldOption {
  value: string;
  label: CustomFieldText;
}

export interface CustomFieldScalarDefinition {
  valueType: CustomFieldValueType;
  options?: readonly CustomFieldOption[];
  // Name of the `StaticDataType` the form offers the values from (the enum itself lives in the backend).
  staticDataType?: string;
  // False for a field whose data is still stored but which the form no longer renders.
  isShownInUi?: boolean;
}

export interface CustomFieldArrayDefinition<TItem = Record<string, unknown>> {
  valueType: "array";
  label: CustomFieldText;
  items: { [K in keyof TItem]-?: CustomFieldScalarDefinition };
}

export type CustomFieldDefinition = CustomFieldScalarDefinition | CustomFieldArrayDefinition;

// An array item's `id` is a technical key the form keeps hidden (like `customFieldLabelMapping`, which omits it).
type CustomFieldDefinitionsOf<TGroup> = {
  [K in keyof TGroup]-?: TGroup[K] extends (infer TItem)[]
    ? CustomFieldArrayDefinition<Omit<TItem, "id">>
    : CustomFieldScalarDefinition;
};

export interface CustomFieldGroupDefinition<TGroup> {
  sectionLabel: CustomFieldText;
  fields: CustomFieldDefinitionsOf<TGroup>;
}

type CustomFieldDefinitions = {
  [G in keyof ICustomFieldsModel]-?: CustomFieldGroupDefinition<ICustomFieldsModel[G]>;
};

const RETIRED_SCORE: CustomFieldScalarDefinition = { valueType: "number", isShownInUi: false };

/**
 * What the organization-specific custom field forms offer: value types, option lists and section labels, as the
 * frontend's `custom-formly-definition.ts` files render them. The backend (Process AI) reads the fields from here;
 * the frontend's custom-fields-declaration.spec.ts holds the forms to it.
 */
export const customFieldDefinitions: CustomFieldDefinitions = {
  stFranziskus: {
    sectionLabel: { translationKey: "customFields.SFS-Daten" },
    fields: {
      educationalAttribution: {
        valueType: "string",
        options: [
          { value: "IR", label: { translationKey: "customFields.educationalAttribution.option1" } },
          { value: "IQ", label: { translationKey: "customFields.educationalAttribution.option2" } },
        ],
      },
      dateOfCommission: { valueType: "date" },
      responsibleForOnboarding: { valueType: "string" },
      vehicleForOnboarding: { valueType: "string" },
      meetingPointForOnboarding: { valueType: "string" },
      pickupTime: { valueType: "dateTime" },
    },
  },
  talentCountry: {
    sectionLabel: { translationKey: "customFields.talentCountrySpecific.title" },
    fields: {
      // Typed `string` in the model, but the form edits and stores it as a number.
      numberOfResidents: { valueType: "number" },
    },
  },
  defa: {
    sectionLabel: { translationKey: "customFields.aboutMe.title" },
    fields: {
      expectationWorkliveInGermany: { valueType: "string" },
      whyStopWorkingForExistingEmployer: { valueType: "string" },
      basicCalculationScore: RETIRED_SCORE,
      percentageCalculationScore: RETIRED_SCORE,
      spatialThinkingScore: RETIRED_SCORE,
      analysisScore: RETIRED_SCORE,
      teamworkSkillScore: RETIRED_SCORE,
      stressHandlungSkillScore: RETIRED_SCORE,
      communicationSkillScore: RETIRED_SCORE,
      assertivenessScore: RETIRED_SCORE,
    },
  },
  dekra: {
    sectionLabel: { text: "Fakturierungsstatus" },
    fields: {
      fakturState: {
        valueType: "string",
        options: [
          { value: "DQ_TN_ZUR_FAKT", label: { text: "DQ – TN zur Faktur melden" } },
          { value: "LOI_AV_FAKT", label: { text: "LOI / AV fakturieren" } },
          { value: "LOI_AV_FAKD", label: { text: "LOI / AV fakturiert" } },
          { value: "B1_B2_FAKT", label: { text: "B1 / B2 fakturieren" } },
          { value: "B1_B2_FAKD", label: { text: "B1 / B2 fakturiert" } },
          { value: "AA_FAKT", label: { text: "Arbeitsantritt fakturieren" } },
          { value: "AA_FAKD", label: { text: "Arbeitsantritt fakturiert" } },
          { value: "FKP_FAKT", label: { text: "FKP / Berufsurkunde fakturieren" } },
          { value: "FKP_FAKD", label: { text: "FKP / Berufsurkunde fakturiert" } },
          { value: "ERSATZKANDIDAT_OHNE_FAKT", label: { text: "Ersatzkandidat ohne Faktur" } },
          { value: "RE_VERANL", label: { text: "Rückerstattung veranlassen" } },
          { value: "RE_VERANLT", label: { text: "Rückerstattung veranlasst" } },
          { value: "ABGESCHL", label: { text: "Abgeschlossen" } },
          { value: "AUSGESCH", label: { text: "Ausgeschieden" } },
        ],
      },
      fakturDate: { valueType: "date" },
      fakturType: {
        valueType: "string",
        options: [
          { value: "FH", label: { text: "Fachhelfer / sonst. Berufe" } },
          { value: "PFK_OFKP", label: { text: "PFK in Anerkennung oFKP" } },
          { value: "PFK_MFKP", label: { text: "PFK in Anerkennung mFKP" } },
        ],
      },
    },
  },
  medwing: {
    sectionLabel: { translationKey: "customFields.languagePerformance" },
    fields: {
      languageProgressTracking: {
        valueType: "array",
        label: { translationKey: "languageTrainingTracking.title" },
        items: {
          module: { valueType: "string", staticDataType: "LanguageModules" },
          presenceRatio: { valueType: "number" },
          comment: { valueType: "string" },
        },
      },
    },
  },
  gpCare: {
    sectionLabel: { translationKey: "customFields.interviewResponsible.title" },
    fields: {
      interviewResponsible: {
        valueType: "string",
        options: [
          { value: "JMeissner", label: { text: "Jörg Meissner" } },
          { value: "MVillon", label: { text: "Mark Villon" } },
        ],
      },
    },
  },
  amalCare: {
    sectionLabel: { translationKey: "customFields.simulationCourse.title" },
    fields: {
      simulationCourseCompleted: { valueType: "boolean" },
    },
  },
};
